import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  effect,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { BaseCssConfig, cssToNgStyle } from '@finxone-platform/shared/sys-config-types';
import {
  EmblaCarouselDirective,
  EmblaCarouselType,
  EmblaEventType,
  EmblaOptionsType,
} from 'embla-carousel-angular';
import Autoplay from 'embla-carousel-autoplay';

export interface CarouselSlide {
  [key: string]: any;
}

@Component({
  selector: 'finxone-web-frontend-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, EmblaCarouselDirective, CommonModule, MatIcon],
})
export class CarouselComponent implements OnDestroy, OnInit {
  @Output() slideChange = new EventEmitter<number>();
  @Input() slides: CarouselSlide[] = [];
  @Input() slideTemplate!: TemplateRef<any>;
  @Input() dotActiveSrc = '/assets/images/indicator-active.svg';
  @Input() dotInactiveSrc = '/assets/images/indicator.svg';
  @Input() carouselStyle: { [key: string]: any } = {};
  @Input() isWorkflow: boolean;
  @Input() baseCssConfig: BaseCssConfig;
  @Input() carouselSlideConfigs: { [key: string]: string }[] = [];
  @Input() emblaOptions: EmblaOptionsType = {};
  @Input() carouselConfig: any;
  @Input() carouselSlidesContainerStyle = {};

  private activePaginationIndicatorStyle = {};
  private inactivePaginationIndicatorStyle = {};
  public activeIndex = 0;
  public readonly subscribeToEvents: EmblaEventType[] = ['select', 'slideFocus'];
  private emblaRef = viewChild<EmblaCarouselDirective>(EmblaCarouselDirective);
  private emblaApi?: EmblaCarouselType;
  public plugins: any[] = [];

  constructor(private cdr: ChangeDetectorRef) {
    effect(() => {
      const directive = this.emblaRef();
      if (!directive) return;

      setTimeout(() => {
        this.emblaApi = directive.emblaApi;
        if (!this.emblaApi) return;

        this.plugins = this.buildPlugins();
        this.reInitEmbla();
        this.cdr.markForCheck();
      });
    });
  }

  ngOnInit() {
    this.setPaginationIndicatorStyles();
  }

  private reInitEmbla(): void {
    if (!this.emblaApi) return;

    if (this.isWorkflow && !this.carouselConfig.previewAutoScrollInWorkflow) {
      this.emblaApi.reInit(this.emblaOptions);
      return;
    }

    if (!this.isWorkflow && !this.plugins?.length) {
      this.emblaApi.reInit(this.emblaOptions);
      return;
    }

    this.emblaApi.reInit(this.emblaOptions, this.plugins);
  }

  private buildPlugins() {
    if (!this.carouselConfig?.isAutoScrollEnabled) return [];
    const delay = Number(this.carouselConfig.autoScrollInterval) * 1000;
    return [Autoplay({ delay })];
  }

  public getSlideStyle(index: number): Record<string, string> {
    return cssToNgStyle(this.carouselSlideConfigs?.[index]?.['outerCssConfig'] || '');
  }

  public scrollTo(index: number): void {
    const directive = this.emblaRef();
    if (!directive || !this.slides?.length) return;

    directive.scrollTo(index);
    this.setActive(index);
  }

  public onEmblaChange(event: EmblaEventType): void {
    if ((event === 'select' || event === 'slideFocus') && this.emblaApi) {
      this.setActive(this.emblaApi.selectedScrollSnap());
    }
  }

  private setActive(index: number): void {
    this.activeIndex = index;
    this.slideChange.emit(index);
    this.cdr.markForCheck();
  }

  public setPaginationIndicatorStyles() {
    this.activePaginationIndicatorStyle = {
      color: this.carouselConfig?.activePaginationIndicatorColor || '#111827',
    };
    this.inactivePaginationIndicatorStyle = {
      color: this.carouselConfig?.inactivePaginationIndicatorColor || '#E5E7EB',
    };
  }

  public getPaginationIndicatorStyle(index: number) {
    return index === this.activeIndex
      ? this.activePaginationIndicatorStyle
      : this.inactivePaginationIndicatorStyle;
  }

  ngOnDestroy(): void {
    this.emblaApi?.destroy();
  }
}
